/**
 * The graph window's class diagram: the same graph as the nodal view, as UML boxes in layers
 * (`state/diagram/` lays it out, `paint.ts` draws it, `gestures.ts` handles the pointer).
 * Selection, filters, search, the right-click menu and the link menu are the nodal view's,
 * so the inspector and the other windows work the same in both.
 *
 * With `blueprint`, the same boxes are a blueprint: "+" adds a pin to a box (a choice, named
 * "Choice 1", "Choice 2"...), a pin dragged onto a box is wired there (a link from that pin, of
 * the kind the environment plays; a pin holds one wire, so wiring it again moves its wire), onto
 * empty space opens Add node for the node it leads to. A pin is renamed in place (double-click,
 * or its right-click menu, which also disconnects or removes it: its wire goes with it, one undo).
 */
import { Show, createEffect, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import type { Pin } from "@protocol/Pin";
import { useApp } from "../../../app/context";
import { toast } from "../../../shared/toast";
import { PACKAGE_TAB, PIN_LINE, selectionFocus, wireFrom, type DiagramBox, type DiagramFocus, type DiagramHit } from "../../../state/diagram";
import type { SimNode } from "../../../state/scene";
import { graphMenu } from "../menu";
import { readPalette, missingColours } from "../palette";
import { isLink } from "../picture";
import type { Target } from "../pointer";
import { pinEdits } from "../../../state/pinEdits";
import { selectedNodeIds } from "../../../state/view";
import { arrangeSelection } from "./arrange";
import { createGestures, type Point } from "./gestures";
import { diagramColours, paintDiagram, type DiagramPalette } from "./paint";
import { isImagePath } from "../../../shared/assets";
import { createPictureCache } from "../images";
import { spotIn } from "../spot";
import { nodeClipboard } from "../nodeClipboard";

/** What the view tells the graph window. */
export interface DiagramViewProps {
  /** Drawn as a blueprint: pins and wires. */
  blueprint: boolean;
  /** A link was drawn between two boxes: the window asks which kind. */
  linked(from: SimNode, to: SimNode, at: Point): void;
  /** A press started (the window closes its link menu). */
  pressed(): void;
  /** Gives the window a way to cancel the gesture in progress (Escape). */
  provideCancel(cancel: () => boolean): void;
  /** Zooms around the centre (shared with the zoom bar). */
  zoom(factor: number): void;
}

/** The class diagram. */
export function DiagramView(props: DiagramViewProps): JSX.Element {
  const { diagram, view, graph, scene, board, actions, access, wm, nodeAdder, menu: contextMenu, presence, kinds, play, assets } = useApp();
  /** Copy and paste of boxes from the right-click menu (the keys are the graph window's). */
  const clipboard = nodeClipboard({ graph, view, scene, diagram, board, actions, kinds }, () => canvas.closest<HTMLElement>(".stage") ?? canvas);
  /** Opens the Add node window beside the page point `near`, for a box there (linked from `from`, from its pin `pin`). */
  const addAt = (near: { x: number; y: number }, from: string | null = null, pin: string | null = null) => {
    nodeAdder.open(null, near, from, "", pin);
    nodeAdder.placeAt(spotIn({ view, scene, diagram, board }, canvas.closest<HTMLElement>(".stage") ?? canvas, near.x, near.y));
  };
  /** A box's picture once it is loaded (it repaints the diagram when it arrives). */
  const picture = createPictureCache(assets, () => (diagram.redraw = true));
  // A picture's address arrives after the box is drawn: draw again.
  createEffect(() => {
    for (const n of graph.state.graph.nodes) for (const path of n.attachments ?? []) if (isImagePath(path)) assets.url(path);
    diagram.redraw = true;
  });
  let host!: HTMLDivElement;
  let canvas!: HTMLCanvasElement;
  const canEdit = () => access.can("editor");
  /** The pin being renamed in place, and where its row is on the canvas (CSS pixels). */
  const [renaming, setRenaming] = createSignal<{ node: string; pin: Pin; x: number; y: number; w: number; h: number } | null>(null);

  /** The pins holding a wire, as `node + "\u0000" + pin`. */
  const wired = () => new Set(graph.state.graph.links.filter((l) => l.pin).map((l) => `${l.from}\u0000${l.pin}`));
  const pins = pinEdits({ graph, actions, kinds, play, view });
  /** The words of a pin's row. */
  const pinLabel = pins.label;
  /** A new pin at the bottom of `box`, worded "Choice n". */
  const addPin = async (box: DiagramBox) => {
    if (await pins.add(box.id)) toast(`Added a choice to ${box.node.name}: drag its pin onto where it leads.`);
  };
  /** Wires pin `pin` of `from` to `to`. */
  const wire = async (from: DiagramBox, pin: Pin, to: DiagramBox) => {
    if (await pins.wire(from.id, pin.id, to.id)) toast(`${pinLabel(from.id, pin)} → ${to.node.name}.`);
  };
  /** Starts renaming pin `index` of `box` in place. */
  const rename = (box: DiagramBox, pin: Pin, index: number) => {
    const v = diagram.view;
    setRenaming({ node: box.id, pin, x: v.x + (box.x + box.w / 2) * v.k, y: v.y + (box.y + box.pinsTop + index * PIN_LINE) * v.k, w: (box.w / 2) * v.k, h: PIN_LINE * v.k });
  };
  /** Ends the renaming, saving `label` unless it is unchanged or `null` (cancelled). */
  const finishRename = (label: string | null) => {
    const r = renaming();
    setRenaming(null);
    if (!r || label === null || label.trim() === r.pin.label) return;
    void pins.rename(r.node, r.pin.id, label);
  };

  const gestures = createGestures({
    diagram,
    canEdit,
    linkTool: () => view.state.linkTool,
    selectedNode: () => (view.state.selection?.type === "node" ? view.state.selection.id : null),
    selectedNodes: () => selectedNodeIds(view.state.selection),
    selectNode: view.selectNode,
    selectNodes: view.selectNodes,
    selectLink: view.selectLink,
    selectGroup: view.selectGroup,
    selectBundle: view.selectBundle,
    focus: () => focus(),
    removeLink: (link) => void actions.removeLink(link).then((ok) => ok && toast("Link removed. Press Ctrl+Z to undo.")),
    linked: (from, to, at) => {
      const a = scene.byId.get(from.id);
      const b = scene.byId.get(to.id);
      if (a && b) props.linked(a, b, at);
    },
    linkedToNothing: (from, at) => {
      const r = canvas.getBoundingClientRect();
      addAt({ x: r.left + at.x, y: r.top + at.y }, from.id);
    },
    blueprint: () => props.blueprint,
    wired: (from, pin, to) => void wire(from, pin, to),
    wiredToNothing: (from, pin, at) => {
      const r = canvas.getBoundingClientRect();
      addAt({ x: r.left + at.x, y: r.top + at.y }, from.id, pin.id);
    },
    addPin: (box) => void addPin(box),
    setCursor: (c) => (canvas.style.cursor = c),
    pressed: () => {
      if (renaming()) finishRename(null);
      props.pressed();
    },
  });
  props.provideCancel(gestures.cancel);

  /** What the user looks at: the box hovered or selected, the selected link, package or bundle. */
  const focus = (): DiagramFocus => {
    const chosen = selectionFocus(view.state.selection);
    const hover = gestures.hover();
    const hovered = hover.type === "box" || hover.type === "pin" || hover.type === "plus" ? hover.box.id : null;
    return { ...chosen, node: gestures.linking()?.from.id ?? hovered ?? chosen.node };
  };

  /** A pointer event's position relative to the canvas. */
  const point = (e: MouseEvent): Point => {
    const r = canvas.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  // Anything the picture shows changed: repaint on the next frame.
  createEffect(() => {
    void [view.state.selection, view.state.query, gestures.hover(), gestures.linking(), gestures.band(), graph.categories(), access.can("editor"), props.blueprint];
    diagram.redraw = true;
  });

  onMount(() => {
    const ctx = canvas.getContext("2d")!;
    let palette: DiagramPalette = diagramColours(readPalette());
    let dpr = 1;
    let frameId = 0;

    /** Follows the host's size; the first real size frames the diagram. */
    const resize = () => {
      const rect = host.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      diagram.width = Math.max(1, Math.round(rect.width));
      diagram.height = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(diagram.width * dpr);
      canvas.height = Math.round(diagram.height * dpr);
      if (!diagram.framed && diagram.width > 1 && graph.state.loaded) diagram.fit();
      diagram.redraw = true;
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    /** Re-reads the colours (theme switched, config edited, system went dark). */
    const repaint = () => {
      palette = diagramColours(readPalette());
      diagram.redraw = true;
    };
    const themeObserver = new MutationObserver(repaint);
    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme", "data-config-revision"] });
    const media = matchMedia("(prefers-color-scheme: dark)");
    media.addEventListener("change", repaint);

    /** Paints when something changed (the layout is worked out again only when it must be). */
    const frame = () => {
      if (missingColours(palette).length) repaint();
      if (!diagram.framed && diagram.width > 1 && graph.state.loaded) diagram.fit();
      const layout = diagram.layout();
      if (diagram.redraw) {
        diagram.redraw = false;
        const s = view.state.selection;
        const hover = gestures.hover();
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        const pictured = paintDiagram(ctx, layout, {
          palette,
          fonts: diagram.fonts(),
          categories: graph.categories(),
          kinds: kinds.available(),
          view: diagram.view,
          width: diagram.width,
          height: diagram.height,
          selectedNode: s?.type === "node" ? s.id : null,
          selectedNodes: selectedNodeIds(s),
          selectedLink: s?.type === "link" ? s.link : null,
          selectedGroup: s?.type === "group" ? s.id : null,
          hoverNode: hover.type === "box" || hover.type === "pin" || hover.type === "plus" ? hover.box.id : null,
          hoverLink: hover.type === "edge" ? hover.edge.link : null,
          hoverGroup: hover.type === "package" ? hover.pack.id : null,
          selectedBundle: s?.type === "bundle" ? s.key : null,
          hoverBundle: hover.type === "bundle" ? hover.bundle.key : null,
          focus: focus(),
          query: view.state.query,
          handles: canEdit(),
          linking: gestures.linking(),
          band: gestures.band(),
          blueprint: props.blueprint,
          wired: wired(),
          pinLabel,
          hoverPin: hover.type === "pin" ? { node: hover.box.id, pin: hover.pin.id } : null,
          picture,
        });
        canvas.dataset.boxes = String(layout.boxes.length);
        // How many pictures were painted (boxes on screen), for the browser checks.
        canvas.dataset.pictures = String(pictured);
        canvas.dataset.arranged = String(diagram.arranged());
      }
      frameId = requestAnimationFrame(frame);
    };
    frameId = requestAnimationFrame(frame);
    onCleanup(() => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
      themeObserver.disconnect();
      media.removeEventListener("change", repaint);
    });
  });

  /** The nodal view's object for a diagram hit (the menu and the link menu work on those). */
  const targetOf = (hit: DiagramHit): Target => {
    if (hit.type === "box" || hit.type === "pin" || hit.type === "plus") {
      const node = scene.byId.get(hit.box.id);
      if (node) return { type: "node", node };
    }
    if (hit.type === "edge") {
      const link = scene.links.find((l) => isLink(l, hit.edge.link));
      if (link) return { type: "link", link };
    }
    if (hit.type === "package") {
      const group = scene.groupById.get(hit.pack.id);
      if (group) return { type: "group", group };
    }
    if (hit.type === "bundle") {
      const bundle = scene.bundleByKey.get(hit.bundle.key);
      if (bundle) return { type: "bundle", bundle };
    }
    return { type: "background" };
  };

  /** Right click: selects what is under the pointer and opens its menu. */
  const openMenu = (e: MouseEvent) => {
    props.pressed();
    const at = point(e);
    const hit = gestures.hitAt(at);
    if (hit.type === "pin") {
      view.selectNode(hit.box.id);
      const old = wireFrom(graph.state.graph.links, hit.box.id, hit.pin.id);
      const { box, pin, index } = hit;
      contextMenu.open(e, [
        { heading: `Pin “${pinLabel(box.id, pin)}”` },
        { label: "Rename…", disabled: !canEdit(), action: () => rename(box, pin, index) },
        { label: "Disconnect", disabled: !canEdit() || !old, action: () => void pins.disconnect(box.id, pin.id) },
        { label: "Remove the pin", danger: true, disabled: !canEdit(), action: () => void pins.remove(box.id, pin.id) },
        { separator: true },
        ...contextMenu.general(),
      ]);
      return;
    }
    // A box of the box selection keeps it: the menu is then for all the selected boxes.
    if (hit.type === "box" && !selectedNodeIds(view.state.selection).includes(hit.box.id)) view.selectNode(hit.box.id);
    if (hit.type === "edge") view.selectLink(hit.edge.link);
    if (hit.type === "package") view.selectGroup(hit.pack.id);
    if (hit.type === "bundle") view.selectBundle(hit.bundle.key);
    // The background's "Arrange by group" is for the whole diagram.
    if (hit.type === "background") view.selectNode(null);
    const near = { x: e.clientX, y: e.clientY };
    /** The Add node window beside the click, for a box where the click was. */
    const addNodeHere = () => addAt(near);
    contextMenu.open(
      e,
      graphMenu(
        {
          graph: graph.state.graph,
          categories: graph.categories(),
          kinds,
          actions,
          view,
          wm,
          general: contextMenu.general,
          zoom: props.zoom,
          addNodeHere,
          clipboard: clipboard.menu(near),
          diagram: { selected: selectedNodeIds(view.state.selection), arrangeLabel: diagram.arrangeLabel(), arrange: () => void arrangeSelection(diagram) },
        },
        targetOf(hit),
      ),
    );
  };

  return (
    <div class="stage diagram-stage" ref={host}>
      <canvas
        ref={canvas}
        id="diagram-canvas"
        class="diagram-canvas"
        style={{ cursor: "grab" }}
        aria-label="Class diagram of the architecture. Use the search box and the inspector to browse nodes with the keyboard."
        onPointerDown={(e) => {
          // Capture fails for a pointer the browser no longer tracks; the gesture goes on anyway.
          try {
            canvas.setPointerCapture?.(e.pointerId);
          } catch {
            /* not captured */
          }
          gestures.down(e, point(e));
        }}
        onPointerMove={(e) => gestures.move(e, point(e))}
        onPointerUp={(e) => gestures.up(e, point(e))}
        onPointerCancel={(e) => gestures.up(e, point(e))}
        onPointerLeave={() => {
          gestures.leave();
          presence.moveCursor(null);
        }}
        onWheel={(e) => gestures.wheel(e, point(e))}
        onContextMenu={openMenu}
        onDblClick={(e) => {
          const hit = gestures.hitAt(point(e));
          if (hit.type === "pin" && canEdit()) rename(hit.box, hit.pin, hit.index);
          // A folded package opens; a package's name tab is written in place.
          if (hit.type === "package" && hit.pack.folded !== undefined) void actions.updateGroup(hit.pack.id, { collapsed: false });
          else if (hit.type === "package" && diagram.toWorld(point(e).x, point(e).y).y <= hit.pack.y + PACKAGE_TAB) view.renameGroup(hit.pack.id);
        }}
      />
      <Show when={renaming()}>
        {(r) => (
          <input
            class="input pin-rename"
            id="pin-rename"
            value={r().pin.label}
            aria-label="The pin's words"
            style={{ left: `${r().x}px`, top: `${r().y}px`, width: `${Math.max(120, r().w)}px`, height: `${Math.max(22, r().h)}px` }}
            ref={(el) => queueMicrotask(() => (el.focus(), el.select()))}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") finishRename(e.currentTarget.value);
              if (e.key === "Escape") finishRename(null);
            }}
            onBlur={(e) => finishRename(e.currentTarget.value)}
          />
        )}
      </Show>
    </div>
  );
}
